<script setup>
import { ref, computed, watch } from "vue";
import { useTriviaStore } from "@stores/trivia";

const triviaStore = useTriviaStore();

const visible = computed(
    () =>
        triviaStore.getReady || triviaStore.countdown > 0 || triviaStore.showGo,
);

const pulseKey = ref(0);
watch(
    () => [triviaStore.countdown, triviaStore.showGo],
    ([count, go]) => {
        if (count > 0 || go) pulseKey.value++;
    },
);
</script>

<template>
    <Transition name="fade" appear>
        <div
            v-if="visible"
            class="countdown-panel bg-marine-800 relative flex items-center justify-center overflow-hidden"
        >
            <img
                v-if="triviaStore.currentQuestionImage"
                :src="triviaStore.currentQuestionImage"
                alt=""
                class="absolute inset-0 size-full object-cover"
            />
            <div
                class="from-marine-800/70 to-marine-800/30 absolute inset-0 bg-linear-to-t opacity-50"
            ></div>

            <p
                v-if="triviaStore.currentQuestionImage"
                class="absolute right-0 bottom-4 left-0 text-center text-sm font-medium text-white"
            >
                TIP: Double click to guess the country.
            </p>

            <span
                v-if="triviaStore.getReady"
                class="countdown display-lg relative leading-none font-extrabold text-yellow-400 uppercase"
            >
                Get Ready!
            </span>
            <span
                v-else
                :key="pulseKey"
                :class="[
                    'countdown countdown--pulse display-xl relative leading-none font-extrabold uppercase',
                    triviaStore.showGo
                        ? 'text-temperature-correct'
                        : 'text-yellow-400',
                ]"
            >
                {{ triviaStore.showGo ? "Go!" : triviaStore.countdown }}
            </span>
        </div>
    </Transition>
</template>

<style scoped>
.countdown-panel {
    grid-column: 4 / -9;
    grid-row: 3 / -2;
    z-index: 3;
}

@media (max-width: 1024px) {
    .countdown-panel {
        grid-column: 1 / -1;
        grid-row: 1 / -1;
    }
}

.countdown {
    -webkit-text-stroke-color: var(--color-marine-600);
    paint-order: stroke fill;
    letter-spacing: -4px;
}

.countdown.display-lg {
    text-shadow: 0 0.1364em 0 var(--color-marine-500);
    -webkit-text-stroke-width: 0.0682em;
}

.countdown.display-xl {
    text-shadow: 0 0.06em 0 var(--color-marine-500);
    -webkit-text-stroke-width: 0.03em;
}

.countdown--pulse {
    animation: countdown-pulse 1s ease-in-out;
}

@keyframes countdown-pulse {
    0% {
        opacity: 0;
        transform: scale(0.5);
    }
    20% {
        opacity: 1;
        transform: scale(1);
    }
    80% {
        opacity: 1;
        transform: scale(1);
    }
    100% {
        opacity: 0;
        transform: scale(0.9);
    }
}
</style>
